/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import type { DateDuration } from '@internationalized/date';
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type HTMLAttributes, createContext, forwardRef, useContext } from 'react';
import { useDateFormatter } from 'react-aria';
import { CalendarStateContext, type ContextValue, Provider, RangeCalendarStateContext, useContextProps } from 'react-aria-components';
import { ChevronLeftStrokeIcon } from '../../icons/chevron-left-stroke/chevron-left-stroke.icon';
import { ChevronRightStrokeIcon } from '../../icons/chevron-right-stroke/chevron-right-stroke.icon';
import { CALENDAR_SLOTS } from '../calendar/calendar.component';
import { Heading } from '../heading/heading.component';
import { IconButton, IconButtonContext } from '../icon-button/icon-button.component';

interface CalendarHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'style' | 'className' | 'children'> {
  variant?: 'start' | 'interim' | 'end';
  /**
   * An offset from the beginning of the visible date range that this
   * CalendarHeader should display. Useful when displaying more than one
   * month at a time.
   */
  offset?: DateDuration;
  /** Whether the calendar header should be small. @default false */
  isSmall?: boolean;
  style?: stylex.StyleXStyles;
}

const CalendarHeaderContext = createContext<ContextValue<CalendarHeaderProps, HTMLElement>>({});

const CalendarHeader = forwardRef((props: CalendarHeaderProps, ref: ForwardedRef<HTMLElement>) => {
  [props, ref] = useContextProps(props, ref, CalendarHeaderContext);

  const { style, variant = 'start', offset = { months: 0 }, isSmall = false, ...coreProps } = props;

  const calendarState = useContext(CalendarStateContext);
  const rangeCalendarState = useContext(RangeCalendarStateContext);
  const state = calendarState ?? rangeCalendarState!;

  const formatter = useDateFormatter({ month: 'long', year: 'numeric' });

  const start = formatter.format(state.visibleRange.start.toDate(state.timeZone));
  const end = formatter.format(state.visibleRange.end.toDate(state.timeZone));
  const current = formatter.format(state.visibleRange.start.add(offset).toDate(state.timeZone));

  const isMultiCalendar = start !== end;

  return (
    <header ref={ref} {...stylex.props(styles.base, isSmall && styles.small, style)} {...coreProps}>
      <Provider
        values={[
          [IconButtonContext, {
            slots: {
              [CALENDAR_SLOTS.PREVIOUS_BUTTON]: {
                size: 32,
                variant: 'ghost',
                isRounded: true,
                isDisabled: isMultiCalendar && variant !== 'start',
                style: isMultiCalendar && variant !== 'start' && iconButtonStyles.hidden,
                'aria-label': 'Previous',
              },
              [CALENDAR_SLOTS.NEXT_BUTTON]: {
                size: 32,
                variant: 'ghost',
                isRounded: true,
                isDisabled: isMultiCalendar && variant !== 'end',
                style: isMultiCalendar && variant !== 'end' && iconButtonStyles.hidden,
                'aria-label': 'Next',
              },
            },
          }],
        ]}>
        <IconButton slot={CALENDAR_SLOTS.PREVIOUS_BUTTON}>
          <ChevronLeftStrokeIcon />
        </IconButton>
        <Heading size={16}>{variant === 'start' ? start : variant === 'end' ? end : current}</Heading>
        <IconButton slot={CALENDAR_SLOTS.NEXT_BUTTON}>
          <ChevronRightStrokeIcon />
        </IconButton>
      </Provider>
    </header>
  );
});

const styles = stylex.create({
  base: {
    paddingInline: 14,
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    justifyContent: 'space-between',
    height: 56,
    width: '100%',
  },
  small: {
    paddingInline: 4,
  },
});

const iconButtonStyles = stylex.create({
  hidden: {
    cursor: 'default',
    opacity: 0,
  }
});

export { CalendarHeader, CalendarHeaderContext, type CalendarHeaderProps };
